import React, { useEffect, useMemo, useState } from "react";
import { Crosshair, Lock, RotateCcw, X } from "lucide-react";
import { berthToDrawing, drawingToBerth } from "../lib/positionCoordinates";

const inputClass = "w-full bg-[#050B14] border border-[#1E293B] px-2 py-1.5 text-xs font-mono text-slate-100";

function initialForm(assignment, berth) {
  const local = drawingToBerth(
    berth,
    assignment.planned_position_x,
    assignment.planned_position_y,
  ) || { chainage: 0, offset: 0 };
  const signedOffset = assignment.berth_offset_m ?? local.offset;
  return {
    method: assignment.positioning_method === "berth_position"
      ? "berth_position"
      : "drawing_coordinates",
    x: assignment.planned_position_x ?? "",
    y: assignment.planned_position_y ?? "",
    chainage: assignment.berth_chainage_m ?? local.chainage,
    offset: Math.abs(signedOffset),
    side: signedOffset < 0 ? "water" : "apron",
    orientation: assignment.planned_orientation_deg ?? 0,
    locked: Boolean(assignment.position_locked),
  };
}

export default function PositionInspector({
  assignment,
  asset,
  berth,
  pickedPoint,
  disabled,
  onClose,
  onApply,
  onAutoPosition,
  onPreviewChange,
  onRequestMapPick,
}) {
  const [form, setForm] = useState(() => initialForm(assignment, berth));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(initialForm(assignment, berth));
  }, [assignment, berth]);

  useEffect(() => {
    if (!pickedPoint) return;
    setForm(current => ({
      ...current,
      method: "drawing_coordinates",
      x: Number(pickedPoint.x.toFixed(3)),
      y: Number(pickedPoint.y.toFixed(3)),
    }));
  }, [pickedPoint]);

  const preview = useMemo(() => {
    if (form.method === "berth_position") {
      const signedOffset = Number(form.offset) * (form.side === "water" ? -1 : 1);
      return berthToDrawing(berth, form.chainage, signedOffset);
    }
    const x = Number(form.x);
    const y = Number(form.y);
    return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : null;
  }, [berth, form]);

  useEffect(() => {
    onPreviewChange(preview);
    return () => onPreviewChange(null);
  }, [onPreviewChange, preview]);

  const submit = async () => {
    if (!preview) return;
    setSaving(true);
    try {
      const common = {
        placement_method: form.method,
        planned_orientation_deg: Number(form.orientation) || 0,
        position_locked: form.locked,
        expected_revision: assignment.revision,
      };
      if (form.method === "berth_position") {
        await onApply(assignment, {
          ...common,
          berth_chainage_m: Number(form.chainage),
          berth_offset_m: Number(form.offset),
          berth_side: form.side,
        });
      } else {
        await onApply(assignment, {
          ...common,
          planned_position_x: Number(form.x),
          planned_position_y: Number(form.y),
        });
      }
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const automatic = async () => {
    setSaving(true);
    try {
      if (form.locked) {
        const unlockPayload = {
          placement_method: form.method,
          planned_orientation_deg: Number(form.orientation) || 0,
          position_locked: false,
          expected_revision: assignment.revision,
        };
        if (form.method === "berth_position") {
          Object.assign(unlockPayload, {
            berth_chainage_m: Number(form.chainage),
            berth_offset_m: Number(form.offset),
            berth_side: form.side,
          });
        } else {
          Object.assign(unlockPayload, {
            planned_position_x: preview?.x,
            planned_position_y: preview?.y,
          });
        }
        await onApply(assignment, unlockPayload);
      }
      const completed = await onAutoPosition(assignment.id);
      if (completed !== false) onClose();
    } finally {
      setSaving(false);
    }
  };

  const update = (key, value) => setForm(current => ({ ...current, [key]: value }));
  const invalid = !preview
    || (form.method === "berth_position"
      && (Number(form.chainage) < 0 || Number(form.chainage) > Number(berth?.length_m || Infinity)));

  return (
    <div className="absolute left-3 bottom-3 z-40 panel w-[310px] max-w-[calc(100%-1.5rem)] p-3 shadow-2xl" data-testid="position-inspector">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-sky-400 font-mono">Exact position</div>
          <div className="text-sm font-heading text-slate-100">{asset?.name || "Terminal asset"}</div>
        </div>
        <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-200" aria-label="Close position inspector"><X size={15}/></button>
      </div>

      <label className="block mt-3 text-[10px] uppercase tracking-wider text-slate-500 font-mono">
        Placement method
        <select value={form.method} onChange={event => update("method", event.target.value)} className={`${inputClass} mt-1`} disabled={disabled || saving}>
          <option value="berth_position">Berth chainage</option>
          <option value="drawing_coordinates">Drawing coordinates</option>
        </select>
      </label>

      {form.method === "berth_position" ? (
        <div className="grid grid-cols-2 gap-2 mt-2">
          <label className="text-[10px] text-slate-400">Along berth (m)
            <input type="number" min="0" max={berth?.length_m} step="0.1" value={form.chainage} onChange={event => update("chainage", event.target.value)} className={`${inputClass} mt-1`} disabled={disabled || saving}/>
          </label>
          <label className="text-[10px] text-slate-400">Quay offset (m)
            <input type="number" min="0" step="0.1" value={form.offset} onChange={event => update("offset", event.target.value)} className={`${inputClass} mt-1`} disabled={disabled || saving}/>
          </label>
          <label className="text-[10px] text-slate-400">Side
            <select value={form.side} onChange={event => update("side", event.target.value)} className={`${inputClass} mt-1`} disabled={disabled || saving}>
              <option value="apron">Apron side</option>
              <option value="water">Water side</option>
            </select>
          </label>
          <div className="flex items-end gap-1">
            <button type="button" onClick={() => update("chainage", Math.max(0, Number(form.chainage) - 0.5))} className="panel-2 px-2 py-1.5 text-xs font-mono" disabled={disabled || saving}>−0.5</button>
            <button type="button" onClick={() => update("chainage", Math.min(Number(berth?.length_m || Infinity), Number(form.chainage) + 0.5))} className="panel-2 px-2 py-1.5 text-xs font-mono" disabled={disabled || saving}>+0.5</button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2 mt-2">
          <label className="text-[10px] text-slate-400">Drawing X
            <input type="number" step="0.1" value={form.x} onChange={event => update("x", event.target.value)} className={`${inputClass} mt-1`} disabled={disabled || saving}/>
          </label>
          <label className="text-[10px] text-slate-400">Drawing Y
            <input type="number" step="0.1" value={form.y} onChange={event => update("y", event.target.value)} className={`${inputClass} mt-1`} disabled={disabled || saving}/>
          </label>
          <button type="button" onClick={onRequestMapPick} className="col-span-2 panel-2 px-2 py-1.5 text-xs text-sky-300 inline-flex items-center justify-center gap-1.5" disabled={disabled || saving}>
            <Crosshair size={13}/> Pick a point on the plan
          </button>
        </div>
      )}

      <label className="block mt-2 text-[10px] text-slate-400">Orientation (degrees)
        <input type="number" step="1" value={form.orientation} onChange={event => update("orientation", event.target.value)} className={`${inputClass} mt-1`} disabled={disabled || saving}/>
      </label>
      <label className="mt-2 flex items-center gap-2 text-xs text-slate-300">
        <input type="checkbox" checked={form.locked} onChange={event => update("locked", event.target.checked)} disabled={disabled || saving}/>
        <Lock size={12}/> Lock during automatic replanning
      </label>

      {invalid && <div className="mt-2 text-[10px] text-red-300">Enter a valid position within the configured berth chainage.</div>}
      {disabled && <div className="mt-2 text-[10px] text-amber-300">Positions can only be changed while this port call is a draft.</div>}

      <div className="flex items-center justify-between gap-2 mt-3">
        <button type="button" onClick={automatic} disabled={disabled || saving || !assignment.ship_asset_id} className="panel-2 px-2 py-1.5 text-[10px] text-slate-300 inline-flex items-center gap-1 disabled:opacity-40" title={assignment.ship_asset_id ? "Use the positioning engine" : "Connect this asset to a ship access point first"}>
          <RotateCcw size={11}/> Automatic
        </button>
        <button type="button" onClick={submit} disabled={disabled || saving || invalid} className="bg-sky-600 hover:bg-sky-500 px-3 py-1.5 text-xs text-white disabled:opacity-40" data-testid="apply-position">
          {saving ? "Saving…" : "Apply position"}
        </button>
      </div>
    </div>
  );
}
